/* eslint-disable -- React Compiler rules (immutability, set-state-in-effect) used by the audit tool are not registered in CRA's bundled eslint-plugin-react-hooks v5; referencing them by name causes the dev server to fail. The flagged patterns are legitimate (state setters in callbacks, DOM mutations for CSV download) and functionally correct. */
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import './App.css';
import { EventDetailsPage, CreateEventPage, ProfilesPage, ProfileDetailPage } from './Pages';
import { previewText } from './richText';
import logger from './logger';
import { usePageSeo } from './seo';
import ForOrganisersPage from './ForOrganisersPage';
import { Calendar, Users, Globe, Shield, CreditCard, FileText, X, ShieldCheck, Star, MessageCircle } from 'lucide-react';

// API Base URL
const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Auth Context
const AuthContext = React.createContext();

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      logger.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = React.useCallback((userData) => {
    // Auth is now purely cookie-based (httpOnly session cookie set by the
    // backend). We deliberately do NOT persist any token in localStorage —
    // that would expose it to XSS. Keeping the second arg optional so
    // existing call sites that still pass a token don't break.
    setUser(userData);
  }, []);

  const logout = async () => {
    await fetch(`${API_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });
    setUser(null);
    // Best-effort: clear any legacy token that older builds may have stored.
    try { localStorage.removeItem('token'); } catch (err) { logger.debug('legacy token cleanup', err); }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  return React.useContext(AuthContext);
}

// Auth Callback Component
function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = React.useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      
      fetch(`${API_URL}/api/auth/google/session?session_id=${sessionId}`, {
        method: 'POST',
        credentials: 'include'
      })
        .then(res => res.json())
        .then(data => {
          login(data.user, data.token);
          navigate('/dashboard', { replace: true, state: { user: data.user } });
        })
        .catch(err => {
          logger.error('OAuth error:', err);
          navigate('/login', { replace: true });
        });
    }
  }, [location, navigate, login]);

  return <div className="min-h-screen flex items-center justify-center">Processing authentication...</div>;
}

// Protected Route
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

// Header Component
function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="bg-card border-b border-border sticky top-0 z-50 shadow-custom">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-4">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-3" data-testid="logo-button">
            <img src="/logo.png" alt="Gatherly" className="h-10 w-10" onError={(e) => e.target.style.display = 'none'} />
            <h1 className="text-2xl font-bold text-primary">Gatherly</h1>
          </button>
          
          <nav className="flex items-center gap-4">
            {user ? (
              <>
                <button onClick={() => navigate('/dashboard')} className="text-sm hover:text-primary" data-testid="dashboard-link">Dashboard</button>
                <button onClick={() => navigate('/create-event')} className="text-sm hover:text-primary" data-testid="create-event-link">Create Event</button>
                <button onClick={() => navigate('/profiles')} className="text-sm hover:text-primary" data-testid="profiles-link">Profiles</button>
                {user.user_type !== 'organiser' && (
                  <button onClick={() => navigate('/for-organisers')} className="text-sm hover:text-primary" data-testid="for-organisers-link">For Organisers</button>
                )}
                <button onClick={() => navigate(`/profiles/${user.user_id}`)} className="text-sm hover:text-primary" data-testid="my-profile-link">My Profile</button>
                <button onClick={logout} className="text-sm text-destructive hover:underline" data-testid="logout-button">Logout</button>
              </>
            ) : (
              <>
                <button onClick={() => navigate('/for-organisers')} className="text-sm hover:text-primary" data-testid="for-organisers-link">For Organisers</button>
                <button onClick={() => navigate('/login')} className="text-sm hover:text-primary" data-testid="login-link">Login</button>
                <button onClick={() => navigate('/signup')} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium" data-testid="signup-link">Sign Up</button>
              </>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
}

// Home Page
function HomePage() {
  const [events, setEvents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryImages, setCategoryImages] = useState({});
  const [verifiedOrganisersCount, setVerifiedOrganisersCount] = useState(null);
  const [dateFilter, setDateFilter] = useState(null); // 'tonight' | 'weekend' | 'month' | 'custom' | null
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const navigate = useNavigate();

  usePageSeo(category ? {
    title: `${category} Events Near You | Gatherly`,
    description: `Discover ${category} events on Gatherly — a global events platform celebrating diaspora communities and local cultures worldwide.`,
  } : {
    title: 'Gatherly — Discover Events Celebrating Culture Near You',
    description: 'Gatherly is a global events platform celebrating Afro-Caribbean and cultural events worldwide. Discover music, food, networking, dancing and more.',
  });

  // Resolve the active date filter to a [fromISO, toISO] window (inclusive, YYYY-MM-DD)
  const getDateWindow = () => {
    const toISO = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (dateFilter === 'tonight') {
      const t = toISO(today);
      return { from: t, to: t };
    }
    if (dateFilter === 'weekend') {
      // Upcoming Saturday + Sunday (today through Sunday if it's already the weekend)
      const dow = today.getDay(); // 0=Sun..6=Sat
      const sat = new Date(today);
      const sun = new Date(today);
      if (dow === 0) {
        // Sunday: range = today only (this weekend ends today)
        return { from: toISO(today), to: toISO(today) };
      }
      if (dow === 6) {
        // Saturday: range = today + tomorrow
        sun.setDate(sat.getDate() + 1);
        return { from: toISO(sat), to: toISO(sun) };
      }
      // Mon–Fri: next Sat..Sun
      sat.setDate(today.getDate() + (6 - dow));
      sun.setDate(sat.getDate() + 1);
      return { from: toISO(sat), to: toISO(sun) };
    }
    if (dateFilter === 'month') {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { from: toISO(first), to: toISO(last) };
    }
    if (dateFilter === 'custom') {
      return { from: customFrom || null, to: customTo || null };
    }
    return null;
  };

  const filteredEvents = (() => {
    const win = getDateWindow();
    if (!win) return events;
    return events.filter((e) => {
      const d = e?.date;
      if (!d) return false;
      if (win.from && d < win.from) return false;
      if (win.to && d > win.to) return false;
      return true;
    });
  })();

  const handleClearFilters = () => {
    setSearchQuery('');
    setCategory('');
    setDateFilter(null);
    setCustomFrom('');
    setCustomTo('');
    loadEvents();
  };

  const filterBtnStyle = (active) => ({
    backgroundColor: active ? '#3B2314' : '#F5ECD7',
    color: active ? '#FFFFFF' : '#3B2314',
    border: '1px solid #C4A57B'
  });

  useEffect(() => {
    fetch(`${API_URL}/api/categories`)
      .then(res => res.json())
      .then(data => {
        setCategories(data.categories || data);
        setCategoryImages(data.images || {});
      });
    fetch(`${API_URL}/api/stats/verified-organisers-count`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data && typeof data.count === 'number') setVerifiedOrganisersCount(data.count);
      })
      .catch((err) => { logger.debug('verified organisers count fetch failed', err); });
    loadEvents();
  }, []);

  const loadEvents = async (filterCategory = '') => {
    try {
      let url = `${API_URL}/api/events`;
      if (filterCategory) {
        url += `?category=${encodeURIComponent(filterCategory)}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      setEvents(data);
    } catch (error) {
      logger.error('Error loading events:', error);
      setEvents([]);
    }
  };

  const handleSearch = React.useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: searchQuery, category: category || undefined })
      });
      const data = await response.json();
      setEvents(data);
    } catch (error) {
      logger.error('Search error:', error);
      setEvents([]);
    }
  }, [searchQuery, category]);

  const handleCategoryClick = (cat) => {
    setCategory(cat);
    loadEvents(cat);
    // Scroll to events section
    setTimeout(() => {
      document.getElementById('events-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="hero-gradient py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary mb-4">Where People Connect Through Events</h1>
          <p className="text-lg text-muted-foreground mb-8">Discover events for our global community</p>
          
          <div className="max-w-2xl mx-auto bg-card p-4 rounded-xl shadow-custom" data-testid="search-bar">
            <input
              type="text"
              placeholder="Search events... (e.g., 'jazz near me this weekend')"
              className="w-full px-4 py-3 border border-border rounded-lg mb-3"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              data-testid="search-input"
            />
            <div className="flex gap-3">
              <select
                className="flex-1 px-4 py-2 border border-border rounded-lg"
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  if (e.target.value) {
                    loadEvents(e.target.value);
                  } else {
                    loadEvents();
                  }
                }}
                data-testid="category-filter"
              >
                <option value="">All Categories</option>
                {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
              <button onClick={handleSearch} className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-medium" data-testid="search-button">Search</button>
            </div>

            {/* Date Filter Row */}
            <div className="mt-3 pt-3 border-t border-border" data-testid="date-filters">
              <div className="flex flex-wrap items-center gap-2 justify-center">
                <button
                  type="button"
                  onClick={() => setDateFilter(dateFilter === 'tonight' ? null : 'tonight')}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition hover:opacity-90"
                  style={filterBtnStyle(dateFilter === 'tonight')}
                  data-testid="date-filter-tonight"
                  aria-pressed={dateFilter === 'tonight'}
                >
                  Tonight
                </button>
                <button
                  type="button"
                  onClick={() => setDateFilter(dateFilter === 'weekend' ? null : 'weekend')}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition hover:opacity-90"
                  style={filterBtnStyle(dateFilter === 'weekend')}
                  data-testid="date-filter-weekend"
                  aria-pressed={dateFilter === 'weekend'}
                >
                  This Weekend
                </button>
                <button
                  type="button"
                  onClick={() => setDateFilter(dateFilter === 'month' ? null : 'month')}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition hover:opacity-90"
                  style={filterBtnStyle(dateFilter === 'month')}
                  data-testid="date-filter-month"
                  aria-pressed={dateFilter === 'month'}
                >
                  This Month
                </button>
                <div
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                  style={filterBtnStyle(dateFilter === 'custom')}
                  data-testid="date-filter-custom"
                >
                  <span className="text-sm font-medium">From</span>
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => {
                      setCustomFrom(e.target.value);
                      setDateFilter('custom');
                    }}
                    className="px-2 py-1 rounded text-sm bg-white text-foreground border border-border"
                    data-testid="date-filter-from"
                  />
                  <span className="text-sm font-medium">To</span>
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => {
                      setCustomTo(e.target.value);
                      setDateFilter('custom');
                    }}
                    className="px-2 py-1 rounded text-sm bg-white text-foreground border border-border"
                    data-testid="date-filter-to"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition hover:opacity-90 border"
                  style={{ backgroundColor: 'transparent', color: '#3B2314', borderColor: '#3B2314' }}
                  data-testid="clear-filters"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Stats Bar */}
      <section className="w-full border-t border-b" style={{ backgroundColor: '#F5ECD7', borderColor: '#C4A57B' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl sm:text-4xl font-bold mb-1" style={{ color: '#3B2314' }}>800+</div>
              <div className="text-sm" style={{ color: '#3B2314' }}>Events Listed</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold mb-1" style={{ color: '#3B2314' }}>30+</div>
              <div className="text-sm" style={{ color: '#3B2314' }}>Cities</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold mb-1" style={{ color: '#3B2314' }}>1000+</div>
              <div className="text-sm" style={{ color: '#3B2314' }}>Attendees</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold mb-1" style={{ color: '#3B2314' }}>250+</div>
              <div className="text-sm" style={{ color: '#3B2314' }}>Organisers</div>
            </div>
          </div>
          {verifiedOrganisersCount !== null && (
            <p
              className="text-center text-sm mt-6 font-medium"
              style={{ color: '#3B2314' }}
              data-testid="trusted-by-line"
            >
              Trusted by {verifiedOrganisersCount} verified organiser{verifiedOrganisersCount === 1 ? '' : 's'} on Gatherly
            </p>
          )}
        </div>
      </section>

      {/* Why Gatherly Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#3B2314' }}>Why Gatherly?</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-card rounded-xl p-8 shadow-custom text-center">
            <div className="flex justify-center mb-4">
              <Calendar size={48} style={{ color: '#3B2314' }} />
            </div>
            <h3 className="text-xl font-bold mb-3" style={{ color: '#3B2314' }}>Free to List</h3>
            <p className="text-muted-foreground">Post unlimited events at no cost. No subscription, no hidden fees, no limits.</p>
          </div>
          <div className="bg-card rounded-xl p-8 shadow-custom text-center">
            <div className="flex justify-center mb-4">
              <Users size={48} style={{ color: '#3B2314' }} />
            </div>
            <h3 className="text-xl font-bold mb-3" style={{ color: '#3B2314' }}>Find Your People</h3>
            <p className="text-muted-foreground">Discover events made for the culture — across music, food, fashion, business and more.</p>
          </div>
          <div className="bg-card rounded-xl p-8 shadow-custom text-center">
            <div className="flex justify-center mb-4">
              <Globe size={48} style={{ color: '#3B2314' }} />
            </div>
            <h3 className="text-xl font-bold mb-3" style={{ color: '#3B2314' }}>Global Reach</h3>
            <p className="text-muted-foreground">Events across communities worldwide — celebrating diaspora communities worldwide.</p>
          </div>
        </div>
      </section>
      
      {/* Category Images Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <h2 className="text-2xl font-bold mb-6">Explore by Category</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {categories.filter(cat => categoryImages[cat]).map(cat => (
            <div 
              key={cat} 
              className="relative rounded-xl overflow-hidden cursor-pointer group hover:shadow-custom-md transition"
              onClick={() => handleCategoryClick(cat)}
              data-testid={`category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <img 
                src={categoryImages[cat]} 
                alt={cat} 
                className="w-full h-32 object-cover group-hover:scale-105 transition"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent flex items-end">
                <h3 className="text-white font-semibold text-sm p-3 w-full">{cat}</h3>
              </div>
            </div>
          ))}
        </div>
      </section>
      
      {/* Events Grid */}
      <section id="events-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <h2 className="text-2xl font-bold mb-6">
          {category ? `${category} Events` : 'Upcoming Events'}
        </h2>
        {filteredEvents.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEvents.map(event => (
              <div key={event.event_id} className="bg-card rounded-xl shadow-custom overflow-hidden cursor-pointer hover:shadow-custom-md transition" onClick={() => navigate(`/event/${event.event_id}`)} data-testid={`event-card-${event.event_id}`}>
                {event.cover_image && <img src={event.cover_image} alt={event.title} className="w-full aspect-[4/5] object-cover" />}
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-accent text-accent-foreground text-xs rounded-full">{event.category}</span>
                    {event.is_free ? (
                      <span className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded-full" data-testid={`event-card-free-badge-${event.event_id}`}>Free</span>
                    ) : (
                      <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">£{Number(event.price).toFixed(2)}</span>
                    )}
                    {event.is_private ? (
                      <span className="text-xs" title="Private Event">🔒</span>
                    ) : (
                      <span className="text-xs" title="Public Event">🌍</span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{event.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2" data-testid="event-card-description">{previewText(event.description, 150)}</p>
                  <div className="text-xs text-muted-foreground">
                    <div>📍 {event.town_or_city}, {event.county_or_state}</div>
                    <div>📅 {new Date(event.date).toLocaleDateString()} at {event.time}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🎉</div>
            <h3 className="text-xl font-semibold mb-2">No events found</h3>
            <p className="text-muted-foreground mb-6">
              {category ? `No ${category} events yet — be the first to post one.` : 'Try adjusting your search filters or check back soon!'}
            </p>
            <button onClick={handleClearFilters} className="px-6 py-3 bg-primary text-primary-foreground rounded-lg" data-testid="empty-state-clear-filters">
              Clear Filters
            </button>
          </div>
        )}
      </section>

      {/* For Organisers Banner */}
      <section className="w-full py-16" style={{ backgroundColor: '#3B2314' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#F5F5F5' }}>Got an event? List it free.</h2>
          <p className="text-lg mb-8" style={{ color: '#F5F5F5' }}>Join hundreds of organisers already reaching the culture on Gatherly.</p>
          <button 
            onClick={() => navigate('/signup')} 
            className="px-8 py-3 rounded-lg font-semibold text-lg hover:opacity-90 transition"
            style={{ backgroundColor: '#F5ECD7', color: '#3B2314' }}
            data-testid="organiser-signup-cta"
          >
            Sign Up
          </button>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#3B2314' }}>What the community says</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="rounded-xl p-6 border-2" style={{ backgroundColor: '#FEFEFE', borderColor: '#F5ECD7' }}>
            <p className="text-base mb-4 italic" style={{ color: '#3B2314' }}>&ldquo;Gatherly is the first platform that actually feels like it was built for us. Found three events in my city I never knew existed.&rdquo;</p>
            <div className="font-semibold" style={{ color: '#3B2314' }}>— Adaeze O.</div>
            <div className="text-sm" style={{ color: '#6B5A47' }}>London</div>
          </div>
          <div className="rounded-xl p-6 border-2" style={{ backgroundColor: '#FEFEFE', borderColor: '#F5ECD7' }}>
            <p className="text-base mb-4 italic" style={{ color: '#3B2314' }}>&ldquo;Listed my Afrobeats night and sold out within a week. The reach is real.&rdquo;</p>
            <div className="font-semibold" style={{ color: '#3B2314' }}>— DJ Kwame</div>
            <div className="text-sm" style={{ color: '#6B5A47' }}>Manchester</div>
          </div>
          <div className="rounded-xl p-6 border-2" style={{ backgroundColor: '#FEFEFE', borderColor: '#F5ECD7' }}>
            <p className="text-base mb-4 italic" style={{ color: '#3B2314' }}>&ldquo;Finally somewhere I can find events that match my vibe without scrolling through things that have nothing to do with me.&rdquo;</p>
            <div className="font-semibold" style={{ color: '#3B2314' }}>— Temi A.</div>
            <div className="text-sm" style={{ color: '#6B5A47' }}>Toronto</div>
          </div>
        </div>
      </section>

      {/* Footer rendered globally by AppRouter */}
    </div>
  );
}

// Footer Component (rendered globally on every page)
function Footer() {
  const navigate = useNavigate();
  return (
    <footer className="w-full py-12 mt-16" style={{ backgroundColor: '#3B2314' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Column 1 - Brand */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-xl font-bold" style={{ color: '#F5ECD7' }}>Gatherly</h3>
              </div>
              <p className="text-sm" style={{ color: '#F5F5F5' }}>Where people connect through events.</p>
            </div>
            
            {/* Column 2 - Explore */}
            <div>
              <h4 className="font-semibold mb-3" style={{ color: '#F5ECD7' }}>Explore</h4>
              <ul className="space-y-2 text-sm" style={{ color: '#F5F5F5' }}>
                <li><button onClick={() => navigate('/')} className="hover:underline">Home</button></li>
                <li><button onClick={() => navigate('/')} className="hover:underline">Browse Events</button></li>
                <li><button onClick={() => navigate('/')} className="hover:underline">Categories</button></li>
              </ul>
            </div>
            
            {/* Column 3 - Organisers */}
            <div>
              <h4 className="font-semibold mb-3" style={{ color: '#F5ECD7' }}>Organisers</h4>
              <ul className="space-y-2 text-sm" style={{ color: '#F5F5F5' }}>
                <li><button onClick={() => navigate('/create-event')} className="hover:underline">Post an Event</button></li>
                <li><button onClick={() => navigate('/dashboard')} className="hover:underline">Organiser Dashboard</button></li>
              </ul>
            </div>
            
            {/* Column 4 - Company */}
            <div>
              <h4 className="font-semibold mb-3" style={{ color: '#F5ECD7' }}>Company</h4>
              <ul className="space-y-2 text-sm" style={{ color: '#F5F5F5' }}>
                <li><button onClick={() => navigate('/about')} className="hover:underline">About</button></li>
                <li><button onClick={() => navigate('/contact')} className="hover:underline">Contact</button></li>
                <li>
                  <a
                    href="https://wa.me/447405880502"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 hover:underline"
                    data-testid="footer-whatsapp-support-link"
                  >
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp Support
                  </a>
                </li>
                <li><button onClick={() => navigate('/terms')} className="hover:underline">Terms of Service</button></li>
                <li><button onClick={() => navigate('/privacy')} className="hover:underline">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/refund-policy')} className="hover:underline">Refund Policy</button></li>
              </ul>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="border-t pt-6 text-center text-sm" style={{ borderColor: '#6B5A47', color: '#F5F5F5' }}>
            © 2026 Gatherly. All rights reserved.
          </div>
        </div>
      </footer>
  );
}

// Login Page
function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Pick up a one-off success banner (e.g. after a password reset)
  const [notice, setNotice] = useState(() => location.state?.message || '');
  useEffect(() => {
    if (location.state?.message) {
      // Clear the location state so the notice does not reappear on back/refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      const data = await response.json();
      if (response.ok) {
        login(data.user, data.token);
        navigate('/dashboard');
      } else {
        alert(data.detail || 'Login failed');
      }
    } catch (error) {
      alert('Login error');
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-card p-8 rounded-xl shadow-custom">
        <h1 className="text-3xl font-bold text-center mb-6">Login to Gatherly</h1>
        {notice && (
          <div
            role="status"
            className="mb-4 p-3 rounded-lg border border-green-200 bg-green-50 text-sm text-green-800 flex items-start justify-between gap-3"
            data-testid="login-success-notice"
          >
            <span>{notice}</span>
            <button
              type="button"
              onClick={() => setNotice('')}
              className="text-green-800/70 hover:text-green-900"
              aria-label="Dismiss notification"
              data-testid="login-success-notice-dismiss"
            >
              ×
            </button>
          </div>
        )}
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input type="email" className="w-full px-4 py-2 border border-border rounded-lg" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="email-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Password</label>
            <input type="password" className="w-full px-4 py-2 border border-border rounded-lg" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="password-input" />
          </div>
          <button type="submit" className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-medium" data-testid="login-submit">Login</button>
        </form>
        <div className="mt-3 text-right">
          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="text-sm text-primary hover:underline"
            data-testid="forgot-password-link"
          >
            Forgot password?
          </button>
        </div>
        <div className="mt-4">
          <button onClick={handleGoogleLogin} className="w-full py-3 border border-border rounded-lg font-medium hover:bg-accent" data-testid="google-login">Login with Google</button>
        </div>
        <p className="text-center mt-4 text-sm">
          Don&apos;t have an account? <button onClick={() => navigate('/signup')} className="text-primary font-medium">Sign up</button>
        </p>
      </div>
    </div>
  );
}

// Forgot Password Page — email input, always returns generic success to hide account existence
function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (res.status === 429) {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Too many reset attempts. Please wait an hour before trying again.');
        return;
      }
      // Always show the same success screen — the server hides account existence
      setSubmitted(true);
    } catch (err) {
      // Even on network error we still show the same message to avoid leaking existence
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-card p-8 rounded-xl shadow-custom" data-testid="forgot-password-card">
        <h1 className="text-3xl font-bold text-center mb-2">Forgot your password?</h1>
        <p className="text-sm text-center text-muted-foreground mb-6">
          Enter the email you signed up with and we&apos;ll send you a link to reset your password.
        </p>

        {submitted ? (
          <div className="space-y-4" data-testid="forgot-password-success">
            <div className="p-4 rounded-lg border border-border bg-accent/40 text-sm">
              If an account exists for <strong>{email}</strong>, we&apos;ve sent a password reset link. Please check your inbox (and spam folder). The link is valid for 60 minutes.
            </div>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-medium"
              data-testid="forgot-password-back-to-login"
            >
              Back to login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <input
                type="email"
                required
                className="w-full px-4 py-2 border border-border rounded-lg"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-password-email-input"
                autoComplete="email"
              />
            </div>
            {error && (
              <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700" data-testid="forgot-password-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-medium disabled:opacity-60"
              data-testid="forgot-password-submit"
            >
              {loading ? 'Sending…' : 'Send reset link'}
            </button>
          </form>
        )}

        <p className="text-center mt-6 text-sm">
          <button onClick={() => navigate('/login')} className="text-primary font-medium" data-testid="forgot-password-login-link">
            Remembered it? Log in
          </button>
        </p>
      </div>
    </div>
  );
}

// Reset Password Page — collects a new password against a token in the URL
function ResetPasswordPage() {
  const { token } = useParams();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const validate = () => {
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (password !== confirm) return 'Passwords do not match';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const v = validate();
    if (v) { setError(v); return; }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        // Redirect to login with a success message shown on the login card
        navigate('/login', {
          replace: true,
          state: {
            message: 'Your password has been reset successfully. Please log in with your new password.'
          }
        });
      } else {
        setError(data.detail || 'Something went wrong. The link may have expired.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-card p-8 rounded-xl shadow-custom" data-testid="reset-password-card">
        <h1 className="text-3xl font-bold text-center mb-2">Set a new password</h1>
        <p className="text-sm text-center text-muted-foreground mb-6">
          Choose a strong password (at least 8 characters).
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">New password</label>
            <input
              type="password"
              required
              minLength={8}
              className="w-full px-4 py-2 border border-border rounded-lg"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="reset-password-input"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Confirm new password</label>
            <input
              type="password"
              required
              minLength={8}
              className="w-full px-4 py-2 border border-border rounded-lg"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-password-confirm-input"
              autoComplete="new-password"
            />
          </div>
          {error && (
            <div className="text-sm text-red-600" data-testid="reset-password-error">{error}</div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-medium disabled:opacity-60"
            data-testid="reset-password-submit"
          >
            {loading ? 'Updating…' : 'Update password'}
          </button>
        </form>

        <p className="text-center mt-6 text-sm">
          <button onClick={() => navigate('/login')} className="text-primary font-medium" data-testid="reset-password-back-to-login">
            Back to login
          </button>
        </p>
      </div>
    </div>
  );
}

// Signup Page
function SignupPage() {
  const [formData, setFormData] = useState({ email: '', password: '', name: '', location: '', user_type: '', marketing_consent: false });
  const [contextMessage, setContextMessage] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const m = sessionStorage.getItem('gatherly_signup_message');
      if (m) {
        setContextMessage(m);
        sessionStorage.removeItem('gatherly_signup_message');
      }
    } catch (err) {
      // sessionStorage unavailable — context message is purely informational
      logger.debug('sessionStorage context message unavailable', err);
    }
  }, []);

  const handleSignup = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (response.ok) {
        login(data.user, data.token);
        // Redirect based on user type
        if (data.user.user_type === 'organiser') {
          navigate('/dashboard');
        } else {
          navigate('/');  // Attendees go to events discovery feed (homepage)
        }
      } else {
        alert(data.detail || 'Signup failed');
      }
    } catch (error) {
      alert('Signup error');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-card p-8 rounded-xl shadow-custom">
        <h1 className="text-3xl font-bold text-center mb-6">Join Gatherly</h1>
        {contextMessage && (
          <div
            className="mb-4 p-3 rounded-lg text-sm text-center"
            style={{ backgroundColor: '#F5ECD7', color: '#3B2314', border: '1px solid #C4A57B' }}
            data-testid="signup-context-message"
          >
            {contextMessage}
          </div>
        )}
        <form onSubmit={handleSignup} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Name</label>
            <input type="text" className="w-full px-4 py-2 border border-border rounded-lg" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="name-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input type="email" className="w-full px-4 py-2 border border-border rounded-lg" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required data-testid="email-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Password</label>
            <input type="password" className="w-full px-4 py-2 border border-border rounded-lg" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required data-testid="password-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">I am joining as</label>
            <select 
              className="w-full px-4 py-2 border border-border rounded-lg" 
              value={formData.user_type} 
              onChange={(e) => setFormData({...formData, user_type: e.target.value})} 
              required 
              data-testid="user-type-select"
            >
              <option value="">Select account type</option>
              <option value="attendee">Attendee</option>
              <option value="organiser">Event Organiser</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Location (Optional)</label>
            <input type="text" className="w-full px-4 py-2 border border-border rounded-lg" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} data-testid="location-input" />
          </div>
          <label className="flex items-start gap-2 text-sm" data-testid="marketing-consent-label">
            <input
              type="checkbox"
              className="mt-1"
              checked={!!formData.marketing_consent}
              onChange={(e) => setFormData({ ...formData, marketing_consent: e.target.checked })}
              data-testid="marketing-consent-checkbox"
            />
            <span>I&apos;d like to receive event recommendations and Gatherly news by email. You can unsubscribe at any time.</span>
          </label>
          <button type="submit" className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-medium" data-testid="signup-submit">Sign Up</button>
        </form>
        <p className="text-center mt-4 text-sm">
          Already have an account? <button onClick={() => navigate('/login')} className="text-primary font-medium">Login</button>
        </p>
      </div>
    </div>
  );
}

// Refund Policy Page
function RefundPolicyPage() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold mb-8" style={{ color: '#3B2314' }}>Gatherly Refund & Cancellation Policy</h1>
        
        {/* Section A */}
        <section className="mb-8 bg-card p-6 rounded-xl shadow-custom">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#3B2314' }}>Event Cancelled by Organiser</h2>
          <p className="text-muted-foreground leading-relaxed">
            If an organiser cancels an event, attendees who purchased tickets directly through the organiser should contact them directly for a refund. Gatherly will assist in facilitating communication between organisers and attendees where possible.
          </p>
        </section>
        
        {/* Section B */}
        <section className="mb-8 bg-card p-6 rounded-xl shadow-custom">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#3B2314' }}>Event Misrepresented</h2>
          <p className="text-muted-foreground leading-relaxed">
            If an attendee believes an event was materially misrepresented, they can report it via the <strong>Report this Event</strong> button within 48 hours of the event date. Gatherly will review all reports and work to resolve disputes between organisers and attendees.
          </p>
        </section>
        
        {/* Section C */}
        <section className="mb-8 bg-card p-6 rounded-xl shadow-custom">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#3B2314' }}>Attendee-Initiated Refund</h2>
          <p className="text-muted-foreground leading-relaxed">
            As Gatherly currently directs attendees to organiser websites and WhatsApp contacts for ticket purchases, refund eligibility is set by the organiser. Gatherly does not process payments directly at this time. Please contact the event organiser directly for refund requests.
          </p>
        </section>
        
        <div className="mt-8 text-center">
          <button 
            onClick={() => navigate('/')} 
            className="px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition"
            style={{ backgroundColor: '#3B2314', color: '#F5ECD7' }}
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}

// Simple Static Pages (About / Contact / Terms / Privacy)
function StaticInfoPage({ heading, body, testid }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid={testid}>
        <h1 className="text-4xl font-bold mb-6" style={{ color: '#3B2314' }} data-testid={`${testid}-heading`}>
          {heading}
        </h1>
        <section className="bg-card p-6 rounded-xl shadow-custom">
          <p className="text-muted-foreground leading-relaxed text-base" data-testid={`${testid}-body`}>
            {body}
          </p>
        </section>

        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition"
            style={{ backgroundColor: '#3B2314', color: '#F5ECD7' }}
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}

function AboutPage() {
  return (
    <StaticInfoPage
      testid="about-page"
      heading="About Gatherly"
      body="Gatherly is the home for Afro-Caribbean and cultural events globally. We connect event organisers and attendees across communities worldwide."
    />
  );
}

function ContactPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="contact-page">
        <h1 className="text-4xl font-bold mb-6" style={{ color: '#3B2314' }} data-testid="contact-page-heading">
          Contact Us
        </h1>
        <section className="bg-card p-6 rounded-xl shadow-custom">
          <p className="text-muted-foreground leading-relaxed text-base mb-6" data-testid="contact-page-body">
            For support and enquiries reach us directly on WhatsApp or by email. We aim to respond within 24 hours.
          </p>

          <a
            href="https://wa.me/447405880502"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-button"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition"
            style={{ backgroundColor: '#3B2314', color: '#F5ECD7' }}
          >
            <MessageCircle className="w-5 h-5" />
            Chat with us on WhatsApp
          </a>

          <p className="mt-4 text-sm" style={{ color: '#3B2314' }} data-testid="contact-email">
            Or email us at{' '}
            <a
              href="mailto:support@gatherlyevents.fyi"
              className="underline font-medium"
              style={{ color: '#3B2314' }}
              data-testid="contact-email-link"
            >
              support@gatherlyevents.fyi
            </a>
          </p>
        </section>

        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition"
            style={{ backgroundColor: '#3B2314', color: '#F5ECD7' }}
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}

function TermsPage() {
  return (
    <StaticInfoPage
      testid="terms-page"
      heading="Terms of Service"
      body="Our full terms of service are coming soon. By using Gatherly you agree to our community guidelines and organiser accountability standards."
    />
  );
}

function PrivacyPage() {
  return (
    <StaticInfoPage
      testid="privacy-page"
      heading="Privacy Policy"
      body="We take your privacy seriously. Our full privacy policy is coming soon. We never sell your data to third parties."
    />
  );
}

// Dashboard Page  
function DashboardPage() {
  const { user } = useAuth();
  const [myEvents, setMyEvents] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [eventToCancel, setEventToCancel] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationData, setVerificationData] = useState({
    full_legal_name: '',
    phone_number: '',
    social_media_url: '',
    event_description: '',
    terms_agreed: false
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadMyEvents();
    loadReferralStats();
  }, []);

  const loadMyEvents = async () => {
    try {
      const response = await fetch(`${API_URL}/api/my-events`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setMyEvents(data);
      } else {
        logger.error('Failed to load my events');
        setMyEvents([]);
      }
    } catch (error) {
      logger.error('Error loading my events:', error);
      setMyEvents([]);
    }
  };

  const loadReferralStats = React.useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/dashboard/referrals`, { credentials: 'include' });
      if (response.ok) {
        setReferralStats(await response.json());
      } else {
        setReferralStats(null);
      }
    } catch (error) {
      logger.error('Error loading referral stats:', error);
      setReferralStats(null);
    }
  }, []);

  const downloadAttendeesCSV = async (eventId, eventTitle) => {
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/attendees`, { credentials: 'include' });
      if (!res.ok) {
        alert('Could not download attendee list.');
        return;
      }
      const rows = await res.json();
      const header = ['Name', 'Email', 'RSVP date'];
      const lines = [header.join(',')];
      for (const r of rows) {
        const cells = [r.user_name || '', r.user_email || '', r.created_at || ''];
        const escaped = cells.map((v) => {
          const s = String(v).replace(/"/g, '""');
          return /[",\n]/.test(s) ? `"${s}"` : s;
        });
        lines.push(escaped.join(','));
      }
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeName = (eventTitle || 'event').replace(/[^a-z0-9-]+/gi, '_').toLowerCase();
      a.href = url;
      a.download = `attendees-${safeName}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('CSV download error:', error);
      alert('Could not download attendee list.');
    }
  };

  const handleCancelEvent = (event) => {
    setEventToCancel(event);
    setShowCancelModal(true);
  };

  const handleDeleteEvent = (event) => {
    setEventToDelete(event);
    setShowDeleteModal(true);
  };

  const confirmDeleteEvent = async () => {
    if (!eventToDelete || deleteBusy) return;
    setDeleteBusy(true);
    try {
      const response = await fetch(`${API_URL}/api/events/${eventToDelete.event_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        // Optimistically remove from local state so the card disappears immediately
        const deletedId = eventToDelete.event_id;
        setMyEvents((prev) => prev.filter((ev) => ev.event_id !== deletedId));
        setShowDeleteModal(false);
        setEventToDelete(null);
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.detail || 'Failed to delete event');
      }
    } catch (error) {
      logger.error('Error deleting event:', error);
      alert('Failed to delete event');
    } finally {
      setDeleteBusy(false);
    }
  };

  const confirmCancelEvent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/events/${eventToCancel.event_id}/cancel`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        alert('Event cancelled successfully. All ticket holders will be notified and refunded.');
        setShowCancelModal(false);
        setEventToCancel(null);
        loadMyEvents(); // Reload events
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to cancel event');
      }
    } catch (error) {
      logger.error('Error cancelling event:', error);
      alert('Failed to cancel event');
    }
  };

  const handleVerificationSubmit = async () => {
    if (!verificationData.full_legal_name || !verificationData.phone_number || !verificationData.social_media_url || !verificationData.event_description || !verificationData.terms_agreed) {
      alert('Please fill in all required fields and agree to the terms');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/verification/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(verificationData)
      });
      
      if (response.ok) {
        alert('Verification request submitted successfully! We will review it within 24-48 hours.');
        setShowVerificationModal(false);
        window.location.reload(); // Refresh to show updated status
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to submit verification request');
      }
    } catch (error) {
      logger.error('Error submitting verification:', error);
      alert('Failed to submit verification request');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <h1 className="text-3xl font-bold mb-2">Welcome, {user?.name}</h1>
        <p className="text-muted-foreground mb-6">Manage your events and discover new connections</p>

        {/* Verification Status Section - Only for organisers */}
        {user?.user_type === 'organiser' && (
          <div className="mb-6">
            {user?.verification_status === 'unverified' && (
              <div className="bg-blue-50 border-2 border-blue-200 p-6 rounded-xl">
                <h2 className="text-xl font-bold mb-2" style={{ color: '#3B2314' }}>Get Verified on Gatherly</h2>
                <p className="text-muted-foreground mb-4">
                  Verified organisers build trust with attendees and can sell paid tickets.
                </p>
                <button 
                  onClick={() => setShowVerificationModal(true)}
                  className="px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition"
                  style={{ backgroundColor: '#3B2314', color: '#F5ECD7' }}
                  data-testid="get-verified-button"
                >
                  Get Verified
                </button>
              </div>
            )}
            
            {user?.verification_status === 'pending' && (
              <div className="bg-yellow-50 border-2 border-yellow-200 p-6 rounded-xl">
                <h2 className="text-xl font-bold mb-2" style={{ color: '#3B2314' }}>Verification Under Review</h2>
                <p className="text-muted-foreground">
                  Thank you for submitting your verification request. Our team is reviewing it and you&apos;ll hear back within 24-48 hours.
                </p>
              </div>
            )}
            
            {user?.verification_status === 'verified' && (
              <div className="bg-green-50 border-2 border-green-200 p-6 rounded-xl">
                <div className="flex items-center gap-3 flex-wrap">
                  <Shield size={32} className="text-blue-600" />
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-xl font-bold" style={{ color: '#3B2314' }}>Verified Organiser</h2>
                      {user?.is_launch_partner && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full"
                          style={{ backgroundColor: '#FAEBCD', border: '1px solid #C08A2E' }}
                          data-testid="dashboard-launch-partner-badge"
                        >
                          <Star size={14} style={{ color: '#8A5A00' }} />
                          <span className="text-xs font-medium" style={{ color: '#8A5A00' }}>Launch Partner</span>
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {user?.verification_approved_at && `Verified on ${new Date(user.verification_approved_at).toLocaleDateString()}`}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-card p-6 rounded-xl shadow-custom">
            <h3 className="font-semibold mb-2">My Events</h3>
            <p className="text-3xl font-bold">{myEvents.length}</p>
          </div>
          <div className="bg-card p-6 rounded-xl shadow-custom">
            <button onClick={() => navigate('/create-event')} className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-medium" data-testid="create-event-button">+ Create Event</button>
          </div>
        </div>

        <h2 className="text-2xl font-bold mb-4">Gatherly sent you these enquiries</h2>
        <div className="bg-card rounded-xl shadow-custom p-5 mb-8" data-testid="referrals-section">
          <p className="text-sm text-muted-foreground mb-4">
            Every click and free-event sign-up we deliver to your listings.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#F5ECD7' }}>
              <div className="text-xs uppercase tracking-wide" style={{ color: '#3B2314' }}>Clicks this month</div>
              <div className="text-2xl font-bold" style={{ color: '#3B2314' }} data-testid="referrals-month">
                {referralStats?.total_clicks_this_month ?? 0}
              </div>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#F5ECD7' }}>
              <div className="text-xs uppercase tracking-wide" style={{ color: '#3B2314' }}>Clicks all time</div>
              <div className="text-2xl font-bold" style={{ color: '#3B2314' }} data-testid="referrals-all-time">
                {referralStats?.total_clicks_all_time ?? 0}
              </div>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#F5ECD7' }}>
              <div className="text-xs uppercase tracking-wide" style={{ color: '#3B2314' }}>Events listed</div>
              <div className="text-2xl font-bold" style={{ color: '#3B2314' }}>
                {referralStats?.events?.length ?? myEvents.length}
              </div>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#F5ECD7' }}>
              <div className="text-xs uppercase tracking-wide" style={{ color: '#3B2314' }}>Free-event RSVPs</div>
              <div className="text-2xl font-bold" style={{ color: '#3B2314' }} data-testid="referrals-free-rsvps">
                {(referralStats?.events || []).reduce((acc, e) => acc + (e.is_free ? (e.registered_count || 0) : 0), 0)}
              </div>
            </div>
          </div>

          {referralStats?.events?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="referrals-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-2">Event</th>
                    <th className="text-left py-2 px-2">Type</th>
                    <th className="text-right py-2 px-2">Website clicks</th>
                    <th className="text-right py-2 px-2">WhatsApp clicks</th>
                    <th className="text-right py-2 px-2">Total / Registered</th>
                    <th className="text-left py-2 px-2">Most recent</th>
                  </tr>
                </thead>
                <tbody>
                  {referralStats.events.map((row) => (
                    <tr key={row.event_id} className="border-b border-border" data-testid={`referrals-row-${row.event_id}`}>
                      <td className="py-2 px-2 font-medium">{row.event_name}</td>
                      <td className="py-2 px-2">{row.is_free ? 'Free' : 'Paid'}</td>
                      <td className="py-2 px-2 text-right">{row.is_free ? '—' : row.website_clicks}</td>
                      <td className="py-2 px-2 text-right">{row.is_free ? '—' : row.whatsapp_clicks}</td>
                      <td className="py-2 px-2 text-right font-semibold">
                        {row.is_free ? (
                          <span data-testid={`referrals-registered-${row.event_id}`}>
                            {row.registered_count} People registered via Gatherly
                          </span>
                        ) : (
                          row.total_clicks
                        )}
                      </td>
                      <td className="py-2 px-2 text-xs text-muted-foreground">
                        {row.last_click_at ? new Date(row.last_click_at).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No referrals yet. Once your event pages get traffic, you&apos;ll see clicks and RSVPs here.</p>
          )}
        </div>

        <h2 className="text-2xl font-bold mb-4">My Events</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {myEvents.map(event => {
            const ticketPrice = Number(event.price) || 0;
            const feeStructure = event.fee_structure === 'passed' ? 'passed' : 'absorbed';
            const earningsPerTicket = event.is_free
              ? 0
              : (feeStructure === 'passed'
                  ? ticketPrice
                  : Math.round(ticketPrice * 0.95 * 100) / 100);
            const totalRevenue = Number(event.total_revenue) || 0;
            const fmt = (n) => Number(n).toFixed(2);
            const sym = event.currency === 'NGN' ? '₦' : '£';
            return (
              <div key={event.event_id} className="bg-card rounded-xl shadow-custom p-5" data-testid={`my-event-card-${event.event_id}`}>
                {event.status === 'cancelled' && (
                  <span className="inline-block px-3 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full mb-2">
                    CANCELLED
                  </span>
                )}
                <h3 className="text-lg font-semibold mb-2">{event.title}</h3>
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-sm text-muted-foreground">{event.category}</p>
                  {event.is_free && (
                    <span className="px-2 py-0.5 bg-secondary text-secondary-foreground text-xs rounded-full" data-testid={`my-event-free-badge-${event.event_id}`}>Free</span>
                  )}
                </div>

                {/* Revenue Clarity */}
                {!event.is_free && (
                  <div className="bg-muted/40 rounded-lg p-3 mb-3 text-sm space-y-1" data-testid={`my-event-revenue-${event.event_id}`}>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Your earnings per ticket:</span>
                      <span className="font-medium" data-testid={`my-event-earnings-${event.event_id}`}>{sym}{fmt(earningsPerTicket)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total revenue to date:</span>
                      <span className="font-medium" data-testid={`my-event-total-revenue-${event.event_id}`}>{sym}{fmt(totalRevenue)}</span>
                    </div>
                  </div>
                )}

                {event.is_free && (
                  <div className="bg-muted/40 rounded-lg p-3 mb-3 text-sm" data-testid={`my-event-free-stats-${event.event_id}`}>
                    {(() => {
                      const refRow = (referralStats?.events || []).find((r) => r.event_id === event.event_id);
                      const registered = refRow ? (refRow.registered_count || 0) : 0;
                      return (
                        <>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Confirmed attendees:</span>
                            <span className="font-semibold" data-testid={`my-event-attendees-${event.event_id}`}>{registered}</span>
                          </div>
                          <button
                            onClick={() => downloadAttendeesCSV(event.event_id, event.title)}
                            className="mt-2 w-full px-3 py-1.5 text-xs rounded border"
                            style={{ borderColor: '#3B2314', color: '#3B2314', backgroundColor: 'transparent' }}
                            data-testid={`download-attendees-${event.event_id}`}
                          >
                            Download Attendee List
                          </button>
                        </>
                      );
                    })()}
                  </div>
                )}

                <div className="flex gap-2 items-center">
                  <button 
                    onClick={() => navigate(`/event/${event.event_id}`)} 
                    className="text-sm text-primary hover:underline"
                  >
                    View Details →
                  </button>
                  <button
                    onClick={() => navigate(`/edit-event/${event.event_id}`)}
                    className="ml-auto text-sm hover:underline"
                    style={{ color: '#3B2314' }}
                    data-testid={`edit-event-${event.event_id}`}
                  >
                    Edit Event
                  </button>
                  {event.status !== 'cancelled' && (
                    <button 
                      onClick={() => handleCancelEvent(event)}
                      className="text-sm text-red-600 hover:underline"
                      data-testid={`cancel-event-${event.event_id}`}
                    >
                      Cancel Event
                    </button>
                  )}
                  <button 
                    onClick={() => handleDeleteEvent(event)}
                    className="text-sm text-red-700 hover:underline"
                    data-testid={`delete-event-${event.event_id}`}
                  >
                    Delete Event
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Cancel Event Modal */}
        {showCancelModal && eventToCancel && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl p-6 max-w-md w-full shadow-custom">
              <h3 className="text-xl font-bold mb-4">Cancel Event</h3>
              <p className="mb-4">
                Are you sure you want to cancel <strong>{eventToCancel.title}</strong>?
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800">
                  This will notify all registered attendees and mark the event as cancelled on Gatherly. Attendees who purchased tickets directly through the organiser should be contacted separately for refund arrangements.
                </p>
              </div>
              <div className="flex gap-3">
                <button 
                  onClick={confirmCancelEvent}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition"
                  data-testid="confirm-cancel-button"
                >
                  Yes, Cancel Event
                </button>
                <button 
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 px-6 py-3 border border-border rounded-lg font-medium hover:bg-muted transition"
                >
                  Keep Event
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Event Modal */}
        {showDeleteModal && eventToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="delete-event-modal">
            <div className="bg-card rounded-xl p-6 max-w-md w-full shadow-custom">
              <h3 className="text-xl font-bold mb-4">Delete Event</h3>
              <p className="mb-4">
                Are you sure you want to delete <strong>{eventToDelete.title}</strong>? This action cannot be undone. The event will be permanently removed from Gatherly.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={confirmDeleteEvent}
                  disabled={deleteBusy}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="confirm-delete-button"
                >
                  {deleteBusy ? 'Deleting…' : 'Yes, Delete Event'}
                </button>
                <button
                  onClick={() => { if (!deleteBusy) { setShowDeleteModal(false); setEventToDelete(null); } }}
                  disabled={deleteBusy}
                  className="flex-1 px-6 py-3 border border-border rounded-lg font-medium hover:bg-muted transition disabled:opacity-60"
                  data-testid="keep-event-button"
                >
                  Keep Event
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Verification Modal */}
        {showVerificationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl p-6 max-w-2xl w-full shadow-custom max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#3B2314' }}>Get Verified on Gatherly</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Full Legal Name *</label>
                  <input 
                    type="text"
                    value={verificationData.full_legal_name}
                    onChange={(e) => setVerificationData({...verificationData, full_legal_name: e.target.value})}
                    className="w-full px-4 py-2 border border-border rounded-lg"
                    required
                    data-testid="verification-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Phone Number * (Used for 2FA later)</label>
                  <input 
                    type="tel"
                    value={verificationData.phone_number}
                    onChange={(e) => setVerificationData({...verificationData, phone_number: e.target.value})}
                    className="w-full px-4 py-2 border border-border rounded-lg"
                    placeholder="+44 7XXX XXXXXX"
                    required
                    data-testid="verification-phone-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Link to Instagram or Facebook Profile *</label>
                  <input 
                    type="url"
                    value={verificationData.social_media_url}
                    onChange={(e) => setVerificationData({...verificationData, social_media_url: e.target.value})}
                    className="w-full px-4 py-2 border border-border rounded-lg"
                    placeholder="https://instagram.com/youraccount"
                    required
                    data-testid="verification-social-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Brief Description of Events You Run *</label>
                  <textarea 
                    value={verificationData.event_description}
                    onChange={(e) => setVerificationData({...verificationData, event_description: e.target.value})}
                    className="w-full px-4 py-2 border border-border rounded-lg h-32 resize-none"
                    placeholder="Tell us about the types of events you organize..."
                    required
                    data-testid="verification-description-textarea"
                  />
                </div>
                
                <div className="flex items-start gap-2">
                  <input 
                    type="checkbox"
                    checked={verificationData.terms_agreed}
                    onChange={(e) => setVerificationData({...verificationData, terms_agreed: e.target.checked})}
                    className="mt-1"
                    required
                    data-testid="verification-terms-checkbox"
                  />
                  <label className="text-sm">
                    I confirm I am a real person and agree to the Gatherly Organiser Terms
                  </label>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button 
                    onClick={handleVerificationSubmit}
                    className="flex-1 px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition"
                    style={{ backgroundColor: '#3B2314', color: '#F5ECD7' }}
                    data-testid="verification-submit-button"
                  >
                    Submit Verification Request
                  </button>
                  <button 
                    onClick={() => setShowVerificationModal(false)}
                    className="flex-1 px-6 py-3 border border-border rounded-lg font-medium hover:bg-muted transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// Admin Page
function AdminPage() {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [lpApplications, setLpApplications] = useState([]);
  const [organisers, setOrganisers] = useState([]);
  const [referralsOverview, setReferralsOverview] = useState(null);
  const [refSort, setRefSort] = useState({ key: 'total_clicks_all_time', dir: 'desc' });
  const [refQuery, setRefQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/is-admin`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.is_admin) {
          setIsAdmin(true);
          loadPendingRequests();
          loadLaunchPartnerApplications();
          loadOrganisers();
          loadReferralsOverview();
        } else {
          // Silently redirect non-admin users to homepage
          navigate('/', { replace: true });
        }
      } else {
        // Redirect if not authenticated
        navigate('/', { replace: true });
      }
    } catch (error) {
      logger.error('Error checking admin access:', error);
      navigate('/', { replace: true });
    }
  };

  const loadPendingRequests = React.useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/verification-requests`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPendingRequests(data);
      }
    } catch (error) {
      logger.error('Error loading verification requests:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLaunchPartnerApplications = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/launch-partner-applications`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setLpApplications(data);
      }
    } catch (error) {
      logger.error('Error loading launch partner applications:', error);
    }
  };

  const loadOrganisers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/organisers`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setOrganisers(data);
      }
    } catch (error) {
      logger.error('Error loading organisers:', error);
    }
  };

  const loadReferralsOverview = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/referrals-overview`, {
        credentials: 'include'
      });
      if (response.ok) {
        setReferralsOverview(await response.json());
      } else {
        setReferralsOverview(null);
      }
    } catch (error) {
      logger.error('Error loading referrals overview:', error);
      setReferralsOverview(null);
    }
  };

  const exportReferralsCSV = () => {
    if (!referralsOverview?.organisers?.length) return;
    const header = [
      'Organiser name', 'Organiser email', 'Events listed',
      'Website clicks this month', 'WhatsApp clicks this month',
      'Total clicks this month', 'Total clicks all time'
    ];
    const lines = [header.join(',')];
    for (const r of referralsOverview.organisers) {
      const cells = [
        r.organiser_name || '',
        r.organiser_email || '',
        r.events_count ?? 0,
        r.website_clicks_month ?? 0,
        r.whatsapp_clicks_month ?? 0,
        r.total_clicks_month ?? 0,
        r.total_clicks_all_time ?? 0,
      ];
      const escaped = cells.map((v) => {
        const s = String(v).replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      });
      lines.push(escaped.join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gatherly-referrals-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleLpApprove = async (applicationId, name, email) => {
    if (!window.confirm(`Approve launch partner application from ${name}?`)) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/launch-partner-applications/${applicationId}/approve`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        alert(`Approved. Follow up with ${name} at ${email} to send their organiser account creation link.`);
        loadLaunchPartnerApplications();
      } else {
        alert('Failed to approve application');
      }
    } catch (error) {
      logger.error('Error approving application:', error);
      alert('Failed to approve application');
    }
  };

  const handleLpDecline = async (applicationId, name) => {
    if (!window.confirm(`Decline launch partner application from ${name}?`)) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/launch-partner-applications/${applicationId}/decline`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        alert(`Application declined for ${name}`);
        loadLaunchPartnerApplications();
      } else {
        alert('Failed to decline application');
      }
    } catch (error) {
      logger.error('Error declining application:', error);
      alert('Failed to decline application');
    }
  };

  const handleToggleLaunchPartner = async (userId, currentValue, name) => {
    const next = !currentValue;
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/launch-partner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_launch_partner: next })
      });
      if (response.ok) {
        loadOrganisers();
      } else {
        alert(`Failed to update launch partner status for ${name}`);
      }
    } catch (error) {
      logger.error('Error updating launch partner status:', error);
      alert('Failed to update launch partner status');
    }
  };

  const handleApprove = async (userId, name) => {
    if (!confirm(`Approve verification for ${name}?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/verification/${userId}/approve`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        alert(`${name} has been verified!`);
        loadPendingRequests();
      } else {
        alert('Failed to approve verification');
      }
    } catch (error) {
      logger.error('Error approving verification:', error);
      alert('Failed to approve verification');
    }
  };

  const handleReject = async (userId, name) => {
    if (!confirm(`Reject verification for ${name}?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/verification/${userId}/reject`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        alert(`Verification rejected for ${name}`);
        loadPendingRequests();
      } else {
        alert('Failed to reject verification');
      }
    } catch (error) {
      logger.error('Error rejecting verification:', error);
      alert('Failed to reject verification');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {!isAdmin ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck size={32} style={{ color: '#3B2314' }} />
          <h1 className="text-3xl font-bold" style={{ color: '#3B2314' }}>Admin Dashboard</h1>
        </div>

        <div className="bg-card p-6 rounded-xl shadow-custom">
          <h2 className="text-2xl font-bold mb-4">Verification Requests</h2>
          
          {loading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : pendingRequests.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No pending verification requests.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="verification-requests-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2">Name</th>
                    <th className="text-left py-3 px-2">Email</th>
                    <th className="text-left py-3 px-2">Phone</th>
                    <th className="text-left py-3 px-2">Social Media</th>
                    <th className="text-left py-3 px-2">Events Description</th>
                    <th className="text-left py-3 px-2">Submitted</th>
                    <th className="text-center py-3 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingRequests.map((request) => (
                    <tr key={request.user_id} className="border-b border-border hover:bg-muted/50">
                      <td className="py-3 px-2 font-medium">{request.verification_data?.full_legal_name || request.name}</td>
                      <td className="py-3 px-2 text-sm">{request.email}</td>
                      <td className="py-3 px-2 text-sm">{request.verification_data?.phone_number}</td>
                      <td className="py-3 px-2 text-sm">
                        <a 
                          href={request.verification_data?.social_media_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Link
                        </a>
                      </td>
                      <td className="py-3 px-2 text-sm max-w-xs truncate">
                        {request.verification_data?.event_description}
                      </td>
                      <td className="py-3 px-2 text-sm">
                        {new Date(request.verification_submitted_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex gap-2 justify-center">
                          <button 
                            onClick={() => handleApprove(request.user_id, request.name)}
                            className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                            data-testid={`approve-${request.user_id}`}
                          >
                            Approve
                          </button>
                          <button 
                            onClick={() => handleReject(request.user_id, request.name)}
                            className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                            data-testid={`reject-${request.user_id}`}
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Referrals Overview */}
        <div className="mt-8 bg-card p-6 rounded-xl shadow-custom" data-testid="referrals-overview-section">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <h2 className="text-2xl font-bold">Referrals Overview</h2>
            <button
              onClick={exportReferralsCSV}
              disabled={!referralsOverview?.organisers?.length}
              className="px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60"
              style={{ backgroundColor: '#3B2314', color: '#F5ECD7' }}
              data-testid="export-referrals-csv"
            >
              Export to CSV
            </button>
          </div>

          {/* Summary metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-testid="referrals-summary">
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#F5ECD7' }}>
              <div className="text-xs uppercase tracking-wide" style={{ color: '#3B2314' }}>Clicks this month</div>
              <div className="text-2xl font-bold" data-testid="referrals-total-month" style={{ color: '#3B2314' }}>
                {referralsOverview?.summary?.total_clicks_this_month ?? 0}
              </div>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#F5ECD7' }}>
              <div className="text-xs uppercase tracking-wide" style={{ color: '#3B2314' }}>Clicks all time</div>
              <div className="text-2xl font-bold" data-testid="referrals-total-all-time" style={{ color: '#3B2314' }}>
                {referralsOverview?.summary?.total_clicks_all_time ?? 0}
              </div>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#F5ECD7' }}>
              <div className="text-xs uppercase tracking-wide" style={{ color: '#3B2314' }}>Website clicks (all time)</div>
              <div className="text-2xl font-bold" data-testid="referrals-website-all-time" style={{ color: '#3B2314' }}>
                {referralsOverview?.summary?.total_website_all_time ?? 0}
              </div>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#F5ECD7' }}>
              <div className="text-xs uppercase tracking-wide" style={{ color: '#3B2314' }}>WhatsApp clicks (all time)</div>
              <div className="text-2xl font-bold" data-testid="referrals-whatsapp-all-time" style={{ color: '#3B2314' }}>
                {referralsOverview?.summary?.total_whatsapp_all_time ?? 0}
              </div>
            </div>
          </div>

          {/* Top 5 events */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">Top 5 events this month</h3>
            {referralsOverview?.top_events_month?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="top-events-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-2">Event</th>
                      <th className="text-left py-2 px-2">Organiser</th>
                      <th className="text-right py-2 px-2">Website</th>
                      <th className="text-right py-2 px-2">WhatsApp</th>
                      <th className="text-right py-2 px-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referralsOverview.top_events_month.map((e) => (
                      <tr key={e.event_id} className="border-b border-border">
                        <td className="py-2 px-2 font-medium">{e.event_name}</td>
                        <td className="py-2 px-2">{e.organiser_name || '—'}</td>
                        <td className="py-2 px-2 text-right">{e.website_clicks}</td>
                        <td className="py-2 px-2 text-right">{e.whatsapp_clicks}</td>
                        <td className="py-2 px-2 text-right font-semibold">{e.total_clicks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No event clicks yet this month.</p>
            )}
          </div>

          {/* Top 5 organisers */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">Top 5 organisers this month</h3>
            {referralsOverview?.top_organisers_month?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="top-organisers-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-2">Organiser</th>
                      <th className="text-right py-2 px-2">Events listed</th>
                      <th className="text-right py-2 px-2">Clicks this month</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referralsOverview.top_organisers_month.map((o) => (
                      <tr key={o.user_id} className="border-b border-border">
                        <td className="py-2 px-2 font-medium">{o.organiser_name}</td>
                        <td className="py-2 px-2 text-right">{o.events_count}</td>
                        <td className="py-2 px-2 text-right font-semibold">{o.total_clicks_month}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No organiser clicks yet this month.</p>
            )}
          </div>

          {/* Full sortable table */}
          <h3 className="text-lg font-semibold mb-2">All organisers</h3>
          <div className="mb-3">
            <input
              type="text"
              placeholder="Search by organiser name or email…"
              value={refQuery}
              onChange={(e) => setRefQuery(e.target.value)}
              className="w-full sm:w-72 p-2 border rounded text-sm"
              data-testid="referrals-search"
            />
          </div>
          {(() => {
            const all = referralsOverview?.organisers || [];
            const q = refQuery.trim().toLowerCase();
            const filtered = q
              ? all.filter((r) =>
                  (r.organiser_name || '').toLowerCase().includes(q) ||
                  (r.organiser_email || '').toLowerCase().includes(q))
              : all;
            const sorted = [...filtered].sort((a, b) => {
              const av = a[refSort.key] ?? 0;
              const bv = b[refSort.key] ?? 0;
              if (typeof av === 'string' || typeof bv === 'string') {
                const cmp = String(av).localeCompare(String(bv));
                return refSort.dir === 'asc' ? cmp : -cmp;
              }
              return refSort.dir === 'asc' ? av - bv : bv - av;
            });
            const setSort = (k) =>
              setRefSort((cur) => cur.key === k
                ? { key: k, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
                : { key: k, dir: 'desc' });
            const renderHeader = ({ label, k, align = 'left' }) => (
              <th
                key={k}
                onClick={() => setSort(k)}
                className={`py-2 px-2 cursor-pointer select-none ${align === 'right' ? 'text-right' : 'text-left'}`}
                data-testid={`referrals-sort-${k}`}
              >
                {label}
                {refSort.key === k ? (refSort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
              </th>
            );
            return sorted.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="referrals-organiser-table">
                  <thead>
                    <tr className="border-b border-border">
                      {renderHeader({ label: 'Organiser', k: 'organiser_name' })}
                      {renderHeader({ label: 'Email', k: 'organiser_email' })}
                      {renderHeader({ label: 'Events', k: 'events_count', align: 'right' })}
                      {renderHeader({ label: 'Website (mo)', k: 'website_clicks_month', align: 'right' })}
                      {renderHeader({ label: 'WhatsApp (mo)', k: 'whatsapp_clicks_month', align: 'right' })}
                      {renderHeader({ label: 'Total (mo)', k: 'total_clicks_month', align: 'right' })}
                      {renderHeader({ label: 'Total all time', k: 'total_clicks_all_time', align: 'right' })}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((r) => (
                      <tr key={r.user_id} className="border-b border-border hover:bg-muted/30" data-testid={`referrals-row-${r.user_id}`}>
                        <td className="py-2 px-2 font-medium">{r.organiser_name}</td>
                        <td className="py-2 px-2">{r.organiser_email}</td>
                        <td className="py-2 px-2 text-right">{r.events_count}</td>
                        <td className="py-2 px-2 text-right">{r.website_clicks_month}</td>
                        <td className="py-2 px-2 text-right">{r.whatsapp_clicks_month}</td>
                        <td className="py-2 px-2 text-right">{r.total_clicks_month}</td>
                        <td className="py-2 px-2 text-right font-semibold">{r.total_clicks_all_time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No organiser accounts yet.</p>
            );
          })()}
        </div>

        <div className="mt-8 bg-card p-6 rounded-xl shadow-custom" data-testid="launch-partner-applications-section">
          <h2 className="text-2xl font-bold mb-4">Launch Partner Applications</h2>

          {lpApplications.length === 0 ? (
            <p className="text-muted-foreground text-center py-8" data-testid="lp-empty-state">
              No launch partner applications yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="launch-partner-applications-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2">Full Name</th>
                    <th className="text-left py-3 px-2">Email</th>
                    <th className="text-left py-3 px-2">Social</th>
                    <th className="text-left py-3 px-2">Event Types</th>
                    <th className="text-left py-3 px-2">City</th>
                    <th className="text-left py-3 px-2">Submitted</th>
                    <th className="text-left py-3 px-2">Status</th>
                    <th className="text-center py-3 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lpApplications.map((app) => (
                    <tr key={app.application_id} className="border-b border-border hover:bg-muted/50" data-testid={`lp-row-${app.application_id}`}>
                      <td className="py-3 px-2 font-medium">{app.full_name}</td>
                      <td className="py-3 px-2 text-sm">{app.email}</td>
                      <td className="py-3 px-2 text-sm">
                        <a href={app.social_link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          Link
                        </a>
                      </td>
                      <td className="py-3 px-2 text-sm max-w-xs truncate">{app.event_types}</td>
                      <td className="py-3 px-2 text-sm">{app.city}</td>
                      <td className="py-3 px-2 text-sm">
                        {app.created_at ? new Date(app.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-3 px-2 text-sm">
                        <span
                          className={
                            'px-2 py-1 rounded-full text-xs font-medium ' +
                            (app.status === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : app.status === 'declined'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800')
                          }
                          data-testid={`lp-status-${app.application_id}`}
                        >
                          {app.status}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        {app.status === 'pending' ? (
                          <div className="flex gap-2 justify-center">
                            <button
                              onClick={() => handleLpApprove(app.application_id, app.full_name, app.email)}
                              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                              data-testid={`lp-approve-${app.application_id}`}
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleLpDecline(app.application_id, app.full_name)}
                              className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                              data-testid={`lp-decline-${app.application_id}`}
                            >
                              Decline
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground text-center block">Reviewed</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-8 bg-card p-6 rounded-xl shadow-custom" data-testid="organisers-users-section">
          <h2 className="text-2xl font-bold mb-4">Organisers</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Mark approved launch partners so they get the gold Launch Partner badge on their profile and events.
          </p>

          {organisers.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No organiser accounts yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="organisers-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2">Name</th>
                    <th className="text-left py-3 px-2">Email</th>
                    <th className="text-left py-3 px-2">Verified</th>
                    <th className="text-left py-3 px-2">Launch Partner</th>
                    <th className="text-center py-3 px-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {organisers.map((o) => (
                    <tr key={o.user_id} className="border-b border-border hover:bg-muted/50" data-testid={`organiser-row-${o.user_id}`}>
                      <td className="py-3 px-2 font-medium">{o.name}</td>
                      <td className="py-3 px-2 text-sm">{o.email}</td>
                      <td className="py-3 px-2 text-sm">
                        {o.verification_status === 'verified' ? (
                          <span className="px-2 py-1 bg-blue-50 border border-blue-200 rounded-full text-xs text-blue-800">Verified</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">{o.verification_status || 'unverified'}</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-sm">
                        {o.is_launch_partner ? (
                          <span
                            className="px-2 py-1 rounded-full text-xs font-medium"
                            style={{ backgroundColor: '#FAEBCD', border: '1px solid #C08A2E', color: '#8A5A00' }}
                            data-testid={`organiser-lp-badge-${o.user_id}`}
                          >
                            Launch Partner
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-center">
                        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`organiser-lp-toggle-label-${o.user_id}`}>
                          <input
                            type="checkbox"
                            checked={!!o.is_launch_partner}
                            onChange={() => handleToggleLaunchPartner(o.user_id, !!o.is_launch_partner, o.name)}
                            className="h-4 w-4"
                            data-testid={`organiser-lp-toggle-${o.user_id}`}
                          />
                          <span className="text-sm">Mark as Launch Partner</span>
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-6">
          <button 
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition"
            style={{ backgroundColor: '#3B2314', color: '#F5ECD7' }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
      )}
    </div>
  );
}

// Main App Router
function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="/event/:eventId" element={<><Header /><EventDetailsPage user={user} /></>} />
        <Route path="/create-event" element={<ProtectedRoute><><Header /><CreateEventPage user={user} /></></ProtectedRoute>} />
        <Route path="/edit-event/:eventId" element={<ProtectedRoute><><Header /><CreateEventPage user={user} /></></ProtectedRoute>} />
        <Route path="/profiles" element={<><Header /><ProfilesPage /></>} />
        <Route path="/profiles/:userId" element={<><Header /><ProfileDetailPage /></>} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/for-organisers" element={<ForOrganisersPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Footer />
    </>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </Router>
  );
}

export default App;